export type OfficeHost = 'word' | 'excel' | 'powerpoint' | 'unknown'

/**
 * Resolve the current Office host. Safe to call after `Office.onReady`.
 */
export function detectHost(): OfficeHost {
  const host = Office?.context?.host
  switch (host) {
    case Office.HostType.Word:
      return 'word'
    case Office.HostType.Excel:
      return 'excel'
    case Office.HostType.PowerPoint:
      return 'powerpoint'
    default:
      return 'unknown'
  }
}

export const HOST_LABELS: Record<OfficeHost, string> = {
  word: 'Word',
  excel: 'Excel',
  powerpoint: 'PowerPoint',
  unknown: 'Office'
}

/** Identity of the active document: full path/URL and its basename. */
export interface DocumentFile {
  /** Full path or URL as reported by the host (only when the doc is saved). */
  path: string
  /** Last path segment, e.g. "Q3 Report.docx". */
  name: string
}

/**
 * The active document's file identity via `getFilePropertiesAsync` (supported
 * across all hosts). Returns undefined for an unsaved document or when the read
 * fails. The async getter is promisified so callers can await it alongside the
 * other snapshot reads; it never rejects.
 */
export function getDocumentFile(): Promise<DocumentFile | undefined> {
  return new Promise((resolve) => {
    const doc = Office?.context?.document
    if (!doc?.getFilePropertiesAsync) {
      resolve(undefined)
      return
    }
    doc.getFilePropertiesAsync((result) => {
      const url = result.status === Office.AsyncResultStatus.Succeeded ? result.value?.url : undefined
      if (!url) {
        resolve(undefined)
        return
      }
      // url is the full path/URL; take the last path segment as the file name.
      const segment = url.split(/[\\/]/).pop()
      const name = segment ? decodeURIComponent(segment) : url
      resolve({ path: url, name })
    })
  })
}

const DOCUMENT_ID_SETTING = 'ramp:documentId'

/** crypto.randomUUID needs a recent webview; older Mac Word lacks it. */
function randomDocumentId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

// One document per task pane (Office reloads the add-in on document switch), so a
// module-level cache is scoped to the open document for the pane's lifetime.
let cachedDocumentId: string | undefined

/**
 * The stable per-document id if one is already embedded in the file, else
 * undefined — a pure read that never writes (so passively opening a document is
 * never marked "modified"). Callers fall back to the file name when absent.
 */
export function getDocumentId(): Promise<string | undefined> {
  if (cachedDocumentId) return Promise.resolve(cachedDocumentId)
  return new Promise((resolve) => {
    const existing = Office?.context?.document?.settings?.get?.(DOCUMENT_ID_SETTING)
    if (typeof existing === 'string' && existing) cachedDocumentId = existing
    resolve(cachedDocumentId)
  })
}

/**
 * Like {@link getDocumentId} but mints and persists an id when the file has none,
 * via the add-in settings bag — it survives rename and path/move, unlike
 * `documentName`/`documentPath`. The write marks the document modified, so call
 * this only on real chat intent (not on open). `saveAsync` persists to disk when
 * the document is next saved; the id is stable for this session regardless.
 * Resolves undefined only when the settings API is unavailable. Never rejects.
 */
export function ensureDocumentId(): Promise<string | undefined> {
  if (cachedDocumentId) return Promise.resolve(cachedDocumentId)
  return new Promise((resolve) => {
    const settings = Office?.context?.document?.settings
    if (!settings?.get || !settings.set || !settings.saveAsync) {
      resolve(undefined)
      return
    }
    const existing = settings.get(DOCUMENT_ID_SETTING)
    if (typeof existing === 'string' && existing) {
      cachedDocumentId = existing
      resolve(existing)
      return
    }
    const id = randomDocumentId()
    settings.set(DOCUMENT_ID_SETTING, id)
    settings.saveAsync((result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded && import.meta.env.DEV) {
        console.debug('[ramp-ai] document-id persist failed:', result.error?.message)
      }
      cachedDocumentId = id
      resolve(id)
    })
  })
}

/** Provenance attached to a document scan so its results can be filtered by document. */
export interface DocumentIdentity {
  documentName?: string
  documentPath?: string
  hostName: string
}

/**
 * Identity of the active document for scan provenance: file name + path (when
 * saved) and the host label. Never rejects — an unsaved/unreadable document
 * yields just the host label.
 */
export async function getDocumentIdentity(): Promise<DocumentIdentity> {
  const file = await getDocumentFile()
  return { documentName: file?.name, documentPath: file?.path, hostName: HOST_LABELS[detectHost()] }
}
